// Export utilities for result tables: Arabic-by-default CSV and branded PDF.
// The PDF is produced by rendering an RTL Arabic HTML template to canvas and
// paginating it into a jsPDF document — this way the browser shapes Arabic text
// correctly (jsPDF's built-in fonts have no Arabic glyphs).
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PCU_LOGO_DATA_URL } from '../assets/pcuLogo';
import { arabicColumnLabel, PCU_NAME_AR, PCU_NAME_EN } from './columnLabels';
import { supabase } from './supabase';
import { stripHtml } from './htmlUtils';

// A branding template applied to PDF exports (managed in Document Templates).
export interface DocTemplate {
  header_text?: string | null;
  footer_text?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  font_family?: string | null;
  orientation?: string | null;
  columns?: string | null;     // comma/newline separated column keys to include (empty = all)
  max_rows?: number | null;    // cap on rows included in the PDF (null = all)
}

// The default template (is_default first, then most recently updated). Best-effort.
const fetchDefaultTemplate = async (): Promise<DocTemplate | null> => {
  try {
    const { data } = await supabase
      .from('document_templates')
      .select('header_text,footer_text,logo_url,primary_color,font_family,orientation,columns,max_rows')
      .order('is_default', { ascending: false })
      .order('updated_at', { ascending: false })
      .limit(1);
    return data && data[0] ? (data[0] as DocTemplate) : null;
  } catch {
    return null;
  }
};

const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;

const cellToString = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  const s = String(value);
  // Rich-text HTML fields (news body, board decisions, ...) -> plain-text
  // preview in exports, not raw/escaped tags.
  return HTML_TAG_RE.test(s) ? stripHtml(s) : s;
};

const sanitizeFilename = (name: string) =>
  name.replace(/[^\w؀-ۿ-]+/g, '_').slice(0, 60) || 'export';

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Download an array of row objects as a UTF-8 CSV (Arabic headers, Excel BOM). */
export const exportRowsToCsv = (rows: any[], columns: string[], filenameBase: string): void => {
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const header = columns.map((c) => escape(arabicColumnLabel(c))).join(',');
  const body = rows
    .map((row) => columns.map((c) => escape(cellToString(row[c]))).join(','))
    .join('\n');
  // BOM so Excel reads Arabic/UTF-8 correctly.
  const csv = '﻿' + header + '\n' + body;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${sanitizeFilename(filenameBase)}.csv`);
};

// Build the printable Arabic HTML report (RTL, Cairo font, branded header).
const buildReportHtml = (
  rows: any[],
  columns: string[],
  opts: { title: string; subtitle?: string },
  template?: DocTemplate | null,
): HTMLElement => {
  const color = (template?.primary_color || '#1976d2').trim();
  const font = (template?.font_family || 'Cairo').trim();
  const logo = template?.logo_url || PCU_LOGO_DATA_URL;
  const headerTitle = template?.header_text || PCU_NAME_AR;
  const footerText = template?.footer_text || '';

  const container = document.createElement('div');
  container.dir = 'rtl';
  container.lang = 'ar';
  // A4 width at 96dpi ≈ 794px; fixed width keeps the canvas crisp.
  container.style.cssText = [
    'position:fixed', 'top:-10000px', 'right:0', 'width:794px', 'padding:32px',
    'background:#ffffff', 'box-sizing:border-box',
    `font-family:'${font}','Cairo','Segoe UI',Tahoma,Arial,sans-serif`, 'color:#1a1a1a',
  ].join(';');

  const headerCells = columns
    .map((c) => `<th style="background:${color};color:#fff;font-weight:700;padding:8px 10px;border:1px solid ${color};font-size:12px;text-align:right;">${escapeHtml(arabicColumnLabel(c))}</th>`)
    .join('');

  const bodyRows = rows
    .map((row, i) => {
      const bg = i % 2 ? '#f5f7fa' : '#ffffff';
      const cells = columns
        .map((c) => `<td style="padding:7px 10px;border:1px solid #e0e0e0;font-size:11px;text-align:right;vertical-align:top;word-break:break-word;">${escapeHtml(cellToString(row[c]))}</td>`)
        .join('');
      return `<tr style="background:${bg};">${cells}</tr>`;
    })
    .join('');

  container.innerHTML = `
    <div style="display:flex;align-items:center;gap:14px;border-bottom:2px solid ${color};padding-bottom:14px;margin-bottom:16px;">
      <img src="${logo}" alt="PCU" style="width:54px;height:54px;object-fit:contain;" crossorigin="anonymous" />
      <div style="flex:1;">
        <div style="font-size:18px;font-weight:800;color:${color};line-height:1.3;">${escapeHtml(headerTitle)}</div>
        <div style="font-size:11px;color:#888;">${escapeHtml(PCU_NAME_EN)}</div>
      </div>
      <div style="text-align:left;">
        <div style="font-size:15px;font-weight:700;">${escapeHtml(opts.title)}</div>
        ${opts.subtitle ? `<div style="font-size:11px;color:#666;">${escapeHtml(opts.subtitle)}</div>` : ''}
      </div>
    </div>
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
    ${footerText
      ? `<div style="margin-top:18px;padding-top:10px;border-top:1px solid ${color};font-size:10px;color:#666;text-align:center;">${escapeHtml(footerText)}</div>`
      : ''}
  `;
  document.body.appendChild(container);
  return container;
};

/**
 * Download rows as a branded, Arabic-correct PDF. Renders an RTL HTML report to
 * canvas (browser shapes Arabic), then paginates the image across A4 pages.
 */
export const exportRowsToPdf = async (
  rows: any[],
  columns: string[],
  opts: { title: string; subtitle?: string; filenameBase: string },
): Promise<void> => {
  const template = await fetchDefaultTemplate();
  // Template can constrain which columns and how many rows appear in the PDF.
  const tplCols = (template?.columns || '')
    .split(/[,\n]/).map((c) => c.trim()).filter(Boolean);
  const effCols = tplCols.length ? tplCols.filter((c) => columns.includes(c)) : columns;
  const effRows = template?.max_rows && template.max_rows > 0 ? rows.slice(0, template.max_rows) : rows;
  const el = buildReportHtml(effRows, effCols.length ? effCols : columns, opts, template);
  try {
    // Wait for the Cairo web font so Arabic renders branded (and shaped) in the canvas.
    if ((document as any).fonts?.ready) {
      try { await (document as any).fonts.ready; } catch { /* ignore */ }
    }
    const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true });

    const orientation = template?.orientation === 'landscape' ? 'landscape' : 'portrait';
    const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;

    if (imgH <= pageH) {
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgW, imgH);
    } else {
      // Slice the tall canvas into page-height chunks.
      const pageCanvasH = Math.floor((canvas.width * pageH) / pageW);
      let rendered = 0;
      let first = true;
      while (rendered < canvas.height) {
        const sliceH = Math.min(pageCanvasH, canvas.height - rendered);
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sliceH;
        const ctx = slice.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, slice.width, slice.height);
          ctx.drawImage(canvas, 0, rendered, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        }
        if (!first) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', 0, 0, imgW, (sliceH * imgW) / canvas.width);
        rendered += sliceH;
        first = false;
      }
    }

    pdf.save(`${sanitizeFilename(opts.filenameBase)}.pdf`);
  } finally {
    document.body.removeChild(el);
  }
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
